import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import Swal from "sweetalert2";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import * as FingerprintJS from "@fingerprintjs/fingerprintjs";
import { API_BASE, API_HOST } from "config/api";

// Define prop types for row data
const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  memberName: PropTypes.string,
  fingerprintsRegistered: PropTypes.number,
  isRegistered: PropTypes.bool,
  originalData: PropTypes.shape({
    member: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      first_name: PropTypes.string,
      surname: PropTypes.string,
      nationality: PropTypes.string,
    }),
    biometrics: PropTypes.array,
  }),
});

// Cell Components
const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const MemberCell = ({ value, row }) => (
  <Box display="flex" alignItems="center" gap={1.5}>
    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{value?.charAt(0)}</Avatar>
    <Box>
      <Typography variant="button" fontWeight={600} display="block">
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {row.original.originalData.member?.nationality || "Member"}
      </Typography>
    </Box>
  </Box>
);
MemberCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
};

const RegistrationStatusCell = ({ value }) => (
  <Box display="flex" justifyContent="center">
    {value ? (
      <CheckIcon color="success" fontSize="medium" />
    ) : (
      <CloseIcon color="error" fontSize="medium" />
    )}
  </Box>
);
RegistrationStatusCell.propTypes = { value: PropTypes.bool.isRequired };

const FingerprintsCountCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value} fingerprints
  </Typography>
);
FingerprintsCountCell.propTypes = { value: PropTypes.number.isRequired };

// Define ActionsCell component outside the hook
const ActionsCell = ({ row, handleRegisterFingerprint }) => {
  const member = row.original.originalData?.member || row.original;
  const isRegistered = row.original.isRegistered;

  return (
    <Box display="flex" justifyContent="center" gap={1}>
      <Button
        variant="contained"
        color={isRegistered ? "info" : "success"}
        size="small"
        startIcon={<FingerprintIcon fontSize="small" />}
        onClick={() => handleRegisterFingerprint(member)}
      >
        {isRegistered ? "Add More" : "Register"}
      </Button>
    </Box>
  );
};
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
  handleRegisterFingerprint: PropTypes.func.isRequired,
};

export default function useBiometricRegistrationData() {
  const { user, church } = useAuth();
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [fingerIndex, setFingerIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleRegisterFingerprint = (member) => {
    // Extract the member data from either the nested structure or direct member object
    const memberData = member.originalData?.member || member;
    console.log("Member data to register:", memberData); // Debug log
    setSelectedMember(memberData);
    setRegistrationModalOpen(true);
  };

  // Create the ActionsWrapper component inside the hook to access handleRegisterFingerprint
  const ActionsWrapper = ({ row }) => (
    <ActionsCell row={row} handleRegisterFingerprint={handleRegisterFingerprint} />
  );
  ActionsWrapper.propTypes = {
    row: PropTypes.shape({ original: rowPropTypes }).isRequired,
  };

  const [biometricData, setBiometricData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "MemberID", accessor: "memberId", align: "left", width: "5%" },
      { Header: "Member", accessor: "memberName", align: "left", width: "30%", Cell: MemberCell },
      {
        Header: "Registered",
        accessor: "isRegistered",
        align: "center",
        width: "15%",
        Cell: RegistrationStatusCell,
      },
      {
        Header: "Fingerprints",
        accessor: "fingerprintsRegistered",
        align: "center",
        width: "15%",
        Cell: FingerprintsCountCell,
      },
      {
        Header: "FingerIndex",
        accessor: "fingerIndex",
        align: "center",
        width: "15%",
        Cell: FingerprintsCountCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "20%",
        Cell: ActionsWrapper,
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const fetchMembersWithBiometrics = async () => {
    try {
      setBiometricData((prev) => ({ ...prev, isLoading: true }));
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE}/biometrics/members/${church.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch members");

      const result = await response.json();
      const members = result.data || result.members || [];

      setBiometricData((prev) => ({
        ...prev,
        rows: members.map((member, index) => ({
          id: member.id,
          serial: index + 1,
          memberId: member.memberID,
          memberName: `${member.first_name} ${member.surname}`,
          isRegistered: member.is_registered,
          fingerprintsRegistered: member.fingerprints_registered,
          fingerIndex: member.finger_index,
          originalData: member,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setBiometricData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
      });
    }
  };

  const registerFingerprint = async () => {
    try {
      setIsProcessing(true);
      const token = localStorage.getItem("authToken");

      if (!selectedMember) {
        throw new Error("No member selected for enrollment");
      }

      const memberData = selectedMember.originalData?.member || selectedMember;
      const memberName =
        memberData.first_name && memberData.surname
          ? `${memberData.first_name} ${memberData.surname}`
          : memberData.memberName || "Unknown Member";

      // The API server calls the Python middleware, which puts the ZKTeco device
      // in enrollment mode. Place the finger on the scanner within 90 seconds.
      const response = await fetch(`${API_BASE}/biometrics`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          member_id: memberData.id,
          finger_index: fingerIndex,
        }),
      });

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(
          responseData.message || responseData.error || `Enrollment failed (${response.status})`
        );
      }

      if (responseData.status !== "success") {
        throw new Error(responseData.message || "Enrollment failed");
      }

      Swal.fire({
        title: "Success!",
        text: `Fingerprint enrolled for ${memberName}`,
        icon: "success",
      });

      fetchMembersWithBiometrics();
      setRegistrationModalOpen(false);
    } catch (error) {
      Swal.fire({
        title: "Enrollment Error",
        text: error.message,
        icon: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (church?.id) {
      fetchMembersWithBiometrics();
    }
  }, [church]);

  return {
    ...biometricData,
    registrationModalOpen,
    setRegistrationModalOpen,
    selectedMember,
    fingerIndex,
    setFingerIndex,
    isProcessing,
    registerFingerprint,
    refresh: fetchMembersWithBiometrics,
  };
}
